import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { BulkBatchDto, BulkRowDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { StatusChip } from '../../components/StatusChip';
import { useBulkRows } from './api';

const RESULT_LABEL = { success: 'Success', failure: 'Failure', pending: 'Pending' } as const;

const COLUMNS: Column<BulkRowDto>[] = [
  { key: 'line', header: 'Line', width: 70, render: (r) => r.line },
  { key: 'key', header: 'Value', nowrap: true, render: (r) => r.key },
  {
    key: 'result',
    header: 'Result',
    render: (r) => <StatusChip status={r.result} label={RESULT_LABEL[r.result]} />,
  },
  { key: 'message', header: 'Message', render: (r) => r.message },
];

function BatchRows({ batch, onClose }: { batch: BulkBatchDto; onClose: () => void }) {
  const [result, setResult] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const rows = useBulkRows(batch.id, { result, page, pageSize });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{batch.name} details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={{ flexGrow: 1 }}>
            Total {batch.total}, success {batch.success}, failure {batch.failure}
          </Typography>
          {/* A filter of one select applies at once; there is nothing else to search by. */}
          <TextField
            select
            label="Result"
            value={result}
            onChange={(e) => {
              setResult(e.target.value);
              setPage(1);
            }}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failure">Failure</MenuItem>
            {/* Rows of a two-step batch that has not been sent yet. */}
            {batch.sendLabel ? <MenuItem value="pending">Pending</MenuItem> : null}
          </TextField>
        </Box>
        <DataTable
          label={`Rows of ${batch.name}`}
          columns={COLUMNS}
          rows={rows.data?.data}
          rowKey={(r) => r.id}
          loading={rows.isFetching}
          error={rows.error}
          onRetry={() => void rows.refetch()}
          page={page}
          pageSize={pageSize}
          total={rows.data?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[20, 50, 100, 200]}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Per-row results of one batch: line, value, result and message, filterable by result. */
export function BatchRowsDialog({
  batch,
  onClose,
}: {
  batch: BulkBatchDto | null;
  onClose: () => void;
}) {
  // `key` resets filter and paging when another batch is opened.
  return batch ? <BatchRows key={batch.id} batch={batch} onClose={onClose} /> : null;
}
